import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { 
  Lock, 
  LockOpen, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Shield,
  FileEdit,
  User,
  Send
} from 'lucide-react';

// Hook to check and manage edit permissions for a patient
export function useEditPermission(patientId) {
  const { api, user } = useAuth();
  const [permission, setPermission] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkPermission = async () => {
    if (!patientId) return;
    try {
      const response = await api.get(`/edit-permissions/check/${patientId}`);
      setPermission(response.data);
    } catch (error) {
      console.error('Failed to check edit permission:', error);
      setPermission({ has_permission: false, reason: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkPermission();
  }, [patientId]);

  return { permission, loading, refresh: checkPermission };
}

// Button component to request edit access
export function RequestEditAccessButton({ patientId, patientName, onPermissionGranted }) {
  const { api, user } = useAuth();
  const [showDialog, setShowDialog] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { permission, loading, refresh } = useEditPermission(patientId);

  // Check if user is an approver (has automatic permission)
  const isApprover = ['director', 'managing_director', 'clinical_lead', 'lead_therapist'].includes(user?.role) || 
                     user?.email?.toLowerCase() === 'admin@icrcl.co.uk';

  if (loading) {
    return (
      <Button variant="outline" disabled className="rounded-full">
        <Lock className="w-4 h-4 mr-2 animate-pulse" />
        Checking permissions...
      </Button>
    );
  }

  // If user already has permission
  if (permission?.has_permission) {
    return (
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          <LockOpen className="w-3 h-3 mr-1" />
          {permission.reason === 'role_based' ? 'Full Edit Access' : 'Edit Access Granted'}
        </Badge>
        {permission.expires_at && (
          <span className="text-xs text-slate-500">
            Expires: {new Date(permission.expires_at).toLocaleString('en-GB')}
          </span>
        )}
      </div>
    );
  }

  // If request is pending
  if (permission?.reason === 'pending') {
    return (
      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
        <Clock className="w-3 h-3 mr-1 animate-pulse" />
        Edit Request Pending Approval
      </Badge>
    );
  }

  const handleRequestAccess = async () => {
    if (!reason.trim()) {
      toast.error('Please provide a reason for your edit request');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/edit-permissions/request', {
        patient_id: patientId,
        reason: reason.trim()
      });

      if (response.data.auto_approved) {
        toast.success('You already have permission to edit');
        onPermissionGranted?.();
      } else {
        toast.success('Edit request submitted! Awaiting approval from Lead Therapist or Super Admin.');
      }
      
      setShowDialog(false);
      setReason('');
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
        onClick={() => setShowDialog(true)}
        data-testid="request-edit-access-btn"
      >
        <Lock className="w-4 h-4 mr-2" />
        Request Edit Access
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileEdit className="w-5 h-5 text-amber-600" />
              Request Edit Access
            </DialogTitle>
            <DialogDescription>
              Request permission to edit information for <strong>{patientName}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Why do I need permission?</p>
                  <p className="text-amber-600 mt-1">
                    To maintain data integrity and comply with safeguarding requirements, 
                    therapists must request approval before editing children's records.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-reason">Reason for Edit Request</Label>
              <Textarea
                id="edit-reason"
                placeholder="Please explain what changes you need to make and why..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                data-testid="edit-request-reason"
              />
              <p className="text-xs text-slate-500">
                This will be reviewed by Lead Therapists or Super Admin.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRequestAccess}
              disabled={submitting || !reason.trim()}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="submit-edit-request-btn"
            >
              {submitting ? (
                <>
                  <Clock className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Panel for approvers to view and manage pending requests
export function EditPermissionApprovalPanel() {
  const { api, user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [selectedHours, setSelectedHours] = useState({});

  // Check if user can approve requests
  const canApprove = ['director', 'managing_director', 'clinical_lead', 'lead_therapist'].includes(user?.role) || 
                     user?.email?.toLowerCase() === 'admin@icrcl.co.uk';

  const fetchRequests = async () => {
    if (!canApprove) return;
    try {
      const response = await api.get('/edit-permissions/pending');
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error('Failed to fetch pending requests:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [canApprove]);

  const handleApprove = async (requestId) => {
    setProcessing(requestId);
    try {
      const validHours = selectedHours[requestId] || 24;
      await api.post('/edit-permissions/approve', {
        request_id: requestId,
        valid_hours: validHours
      });
      toast.success(`Edit permission approved for ${validHours} hours`);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (requestId) => {
    setProcessing(requestId);
    try {
      await api.post(`/edit-permissions/reject?request_id=${requestId}`);
      toast.success('Edit permission request rejected');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setProcessing(null);
    }
  };

  if (!canApprove) return null;
  if (loading) return null;
  if (requests.length === 0) return null;

  return (
    <Card className="card-icrcl border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 mb-6">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <Shield className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <CardTitle className="text-lg text-amber-800">
              Pending Edit Requests ({requests.length})
            </CardTitle>
            <CardDescription className="text-amber-600">
              Therapists requesting permission to edit children's information
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {requests.map((request) => (
            <div
              key={request.id}
              className="p-4 bg-white rounded-lg border border-amber-100"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <User className="w-4 h-4 text-slate-500" />
                    <span className="font-medium text-slate-900">
                      {request.user_name}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {request.user_role?.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 mb-2">
                    Requesting edit access for: <strong>{request.patient_name}</strong>
                  </p>
                  <div className="bg-slate-50 p-2 rounded text-sm text-slate-600">
                    <strong>Reason:</strong> {request.reason}
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    Requested: {new Date(request.requested_at).toLocaleString('en-GB')}
                  </p>
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <Select
                    value={String(selectedHours[request.id] || 24)}
                    onValueChange={(value) => setSelectedHours(prev => ({
                      ...prev,
                      [request.id]: parseInt(value)
                    }))}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 hour</SelectItem>
                      <SelectItem value="4">4 hours</SelectItem>
                      <SelectItem value="8">8 hours</SelectItem>
                      <SelectItem value="24">24 hours</SelectItem>
                      <SelectItem value="48">48 hours</SelectItem>
                      <SelectItem value="72">72 hours</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-200 text-red-600 hover:bg-red-50"
                      onClick={() => handleReject(request.id)}
                      disabled={processing === request.id}
                      data-testid={`reject-edit-request-${request.id}`}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleApprove(request.id)}
                      disabled={processing === request.id}
                      data-testid={`approve-edit-request-${request.id}`}
                    >
                      {processing === request.id ? (
                        <Clock className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-1" />
                      )}
                      Approve
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default RequestEditAccessButton;
